import React, { Fragment, useId, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import { pagesKey } from '@dashboard/lib/queryKeys';
import { cn } from '@dashboard/lib/utils';
import PageMultiSelectPageContainer from '@dashboard/components/Formik/components/PageMultiSelectPageContainer';
import {
  openDashboardLink,
  PageSearchItem,
  PageSearchSkeleton,
} from '@dashboard/components/Formik/components/PageMultiSelect';
import {
  Command,
  CommandEmpty,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@dashboard/components/ui/command';
import { Label } from '@dashboard/components/Form';
import { Input } from '@dashboard/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@dashboard/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@dashboard/components/ui/radio-group';
import { Separator } from '@dashboard/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@dashboard/components/ui/tooltip';
import { pageBumpOptions, productPriceOptions, ticketOption } from './couponPrices';
import PickedNames, { NAME_SEPARATOR } from './PickedNames';
import {
  isEvent,
  newScopePage,
  pageModes,
  PRICES,
  PRICES_AND_BUMPS,
  switchRuleMode,
  takesBumps,
  takesItems,
  TICKETS,
  TICKETS_AND_ADD_ONS,
  WHOLE_ORDER,
} from './scopePage';
import { mergeScopeResults, SCOPE_SEARCH_LIMIT, scopeItemKey } from './scopeSearch';
import { usePageBumps, usePagePrices, usePageTickets } from './useScopeOptions';

export const SEARCH_LABEL = 'Checkouts and events';
const SEARCH_PLACEHOLDER = 'Search checkouts and events';

const CHECKOUT_COPY = {
  modes: {
    [WHOLE_ORDER]: 'Apply to whole order',
    [PRICES]: 'Apply to prices',
    [PRICES_AND_BUMPS]: 'Apply to prices and bumps',
  },
  help: {
    [PRICES]:
      'The coupon applies when a customer buys one of these prices, discounting that price only.',
    [PRICES_AND_BUMPS]:
      'The coupon applies only when a customer buys one of these prices and one of these bumps, discounting both.',
  },
  noBumps: 'Disabled because this checkout has no bumps',
  items: { label: 'Prices', plural: 'prices', all: 'All prices' },
  bumps: { label: 'Bumps', plural: 'bumps', all: 'All bumps' },
};
const EVENT_COPY = {
  modes: {
    [WHOLE_ORDER]: 'Apply to whole event',
    [TICKETS]: 'Apply to tickets',
    [TICKETS_AND_ADD_ONS]: 'Apply to tickets and add-ons',
  },
  help: {
    [TICKETS]:
      'The coupon applies when a customer buys one of these tickets, discounting those tickets only.',
    [TICKETS_AND_ADD_ONS]:
      'The coupon applies only when a customer buys one of these tickets and one of these add-ons, discounting both.',
  },
  noBumps: 'Disabled because this event has no add-ons',
  items: { label: 'Tickets', plural: 'tickets', all: 'All tickets' },
  bumps: { label: 'Add-ons', plural: 'add-ons', all: 'All add-ons' },
};
export const scopeCopy = page => (isEvent(page) ? EVENT_COPY : CHECKOUT_COPY);

const nextOffset = (lastPage, allPages) =>
  lastPage.has_more ? allPages.length * SCOPE_SEARCH_LIMIT : undefined;

const ScopeSearch = ({ pickedKeys, onAddPage }) => {
  const { sellerId } = useAuth();
  const { getPageSummaries } = usePageQueries();
  const { searchInput, setSearchInput, search } = useSearchInput('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const inputId = useId();

  const pagesQuery = useInfiniteQuery({
    queryKey: [pagesKey, 'coupon-scope', sellerId, search],
    queryFn: ({ pageParam }) =>
      getPageSummaries(sellerId, {
        status: ['published', 'draft'],
        type: ['checkout', 'event'],
        limit: SCOPE_SEARCH_LIMIT,
        skip: pageParam,
        search: search === '' ? undefined : search,
      }),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
    enabled: open,
  });

  const { isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } = pagesQuery;
  const rows = mergeScopeResults(
    [{ kind: 'page', batches: pagesQuery.data?.pages?.map(group => group.data) ?? [] }],
    pickedKeys,
  );

  // Not BottomScrollListener: its observer can still read "at the bottom" after a batch lands,
  // which loads batch after batch with the list standing still.
  const loadMoreNearBottom = event => {
    const list = event.currentTarget;
    if (isFetchingNextPage || list.scrollHeight - list.scrollTop - list.clientHeight > 50) return;
    if (hasNextPage) fetchNextPage();
  };

  const close = () => {
    setSearchInput('');
    setOpen(false);
  };

  return (
    <div>
      <Label htmlFor={inputId}>{SEARCH_LABEL}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              id={inputId}
              value={searchInput}
              placeholder={SEARCH_PLACEHOLDER}
              autoComplete="off"
              onChange={e => {
                setSearchInput(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={e => {
                // Enter picks from the list; it must not submit the step.
                if (e.key === 'Enter') e.preventDefault();
              }}
              className="pr-16"
            />
            <div className="absolute inset-y-0 right-2 flex items-center gap-1 pointer-events-none">
              {searchInput.length > 0 && (
                <button
                  type="button"
                  onMouseDown={e => {
                    e.preventDefault();
                    setSearchInput('');
                    inputRef.current?.focus();
                  }}
                  className="text-muted-foreground transition-colors hover:text-foreground pointer-events-auto"
                  aria-label="Clear search"
                >
                  <XMarkIcon className="size-4" />
                </button>
              )}
              <MagnifyingGlassIcon className="size-4 text-muted-foreground/60" />
            </div>
          </div>
        </PopoverAnchor>
        <PopoverContent
          // Inside the edit dialog its scroll lock blocks scrolling a list portaled outside it.
          container={inputRef.current?.closest('[role="dialog"]') ?? undefined}
          className="p-0 w-[var(--radix-popover-trigger-width)]"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={e => e.preventDefault()}
          onInteractOutside={e => {
            if (inputRef.current?.contains(e.target)) e.preventDefault();
          }}
        >
          <Command shouldFilter={false}>
            <CommandList className="max-h-72 p-1 scrollbar-hide" onScroll={loadMoreNearBottom}>
              {isError && <CommandEmpty>Error loading checkouts and events</CommandEmpty>}
              {!isError && !isLoading && rows.length === 0 && (
                <CommandEmpty>No checkouts or events found</CommandEmpty>
              )}
              {!isLoading &&
                rows.map(({ item, key }) => (
                  <PageSearchItem
                    key={key}
                    page={item}
                    onSelect={page => {
                      onAddPage(page);
                      close();
                    }}
                  />
                ))}
              {(isLoading || isFetchingNextPage) && <PageSearchSkeleton />}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

/**
 * A pick of some of a page's prices, tickets or bumps. None picked means all of them, including
 * ones added later; ticking every option is still a list of those options.
 */
export const ScopeOptionsPicker = ({
  id,
  label,
  ariaLabel,
  allLabel,
  options,
  selectedIds,
  onChange,
  loading = false,
  loadingLabel,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const pickedNames = options
    .filter(option => selectedIds.includes(option.id))
    .map(option => (option.detail ? `${option.name} · ${option.detail}` : option.name));

  const toggle = optionId =>
    onChange(
      selectedIds.includes(optionId)
        ? selectedIds.filter(selectedId => selectedId !== optionId)
        : [...selectedIds, optionId],
    );

  const showsNames = !loading && pickedNames.length > 0;

  const trigger = (
    <button
      ref={triggerRef}
      id={id}
      type="button"
      onClick={() => setOpen(o => !o)}
      disabled={loading || disabled}
      aria-label={ariaLabel}
      className="flex h-8 w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-input bg-transparent dark:bg-input/30 pr-2 pl-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {showsNames ? (
        <PickedNames names={pickedNames} />
      ) : (
        <span className="truncate">{loading ? loadingLabel : allLabel}</span>
      )}
      <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
    </button>
  );

  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <TooltipProvider>
          <Tooltip open={showsNames ? undefined : false}>
            <PopoverAnchor asChild>
              <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            </PopoverAnchor>
            <TooltipContent className="max-w-xs">{pickedNames.join(NAME_SEPARATOR)}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <PopoverContent
          container={triggerRef.current?.closest('[role="dialog"]') ?? undefined}
          className="p-0 w-[var(--radix-popover-trigger-width)]"
          align="start"
          sideOffset={4}
        >
          <Command shouldFilter={false}>
            <CommandList className="max-h-72 p-1">
              <CommandItem value="all" onSelect={() => onChange([])}>
                <span className="flex-1">{allLabel}</span>
                {selectedIds.length === 0 && <CheckIcon className="size-4" aria-label="Selected" />}
              </CommandItem>
              <CommandSeparator className="my-1" />
              {options.map(option => (
                <CommandItem key={option.id} value={option.id} onSelect={() => toggle(option.id)}>
                  <span className="flex-1">
                    {option.name}
                    {option.detail && (
                      <span className="text-muted-foreground"> · {option.detail}</span>
                    )}
                  </span>
                  {selectedIds.includes(option.id) && (
                    <CheckIcon className="size-4" aria-label="Selected" />
                  )}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const WordDivider = ({ word }) => (
  <div className="relative" role="separator" aria-label={word}>
    <div className="absolute inset-0 flex items-center">
      <Separator />
    </div>
    <div className="relative flex justify-center">
      <span className="bg-white dark:bg-background px-2 text-xs text-muted-foreground">{word}</span>
    </div>
  </div>
);

const useItemOptions = (page, selectedIds) => {
  const event = isEvent(page);
  const prices = usePagePrices(page, { enabled: !event });
  const tickets = usePageTickets(page, { enabled: event });
  if (event) {
    return {
      options: tickets.ticketTypes.map(ticket => ticketOption(ticket, page.eventDetails?.currency)),
      isLoading: tickets.isLoading,
    };
  }
  return {
    options: productPriceOptions(prices.product, selectedIds),
    isLoading: prices.isLoading,
  };
};

const RuleItems = ({ page, rule, onChange }) => {
  const { options, isLoading } = useItemOptions(page, rule.itemIds);
  const copy = scopeCopy(page).items;

  return (
    <ScopeOptionsPicker
      id={useId()}
      label={copy.label}
      ariaLabel={`${copy.label} for ${page.name}`}
      allLabel={copy.all}
      loading={isLoading}
      loadingLabel={`Loading ${copy.plural}…`}
      options={options}
      selectedIds={rule.itemIds}
      onChange={itemIds => onChange({ itemIds })}
    />
  );
};

const RuleBumps = ({ page, rule, bumpOptions, onChange }) => {
  const copy = scopeCopy(page).bumps;

  return (
    <ScopeOptionsPicker
      id={useId()}
      label={copy.label}
      ariaLabel={`${copy.label} for ${page.name}`}
      allLabel={copy.all}
      options={bumpOptions}
      selectedIds={rule.bumpIds}
      onChange={bumpIds => onChange({ bumpIds })}
    />
  );
};

const ModeRadio = ({ id, mode, label, disabled, disabledReason }) => {
  const rowRef = useRef(null);
  const radio = (
    <div className="flex items-center gap-x-2">
      <RadioGroupItem id={id} value={mode} disabled={disabled} />
      <Label
        htmlFor={id}
        isInline
        className={cn('font-normal', disabled && 'text-muted-foreground')}
      >
        {label}
      </Label>
    </div>
  );
  if (!disabled || !disabledReason) return radio;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        {/* A disabled radio fires no pointer events, so the trigger needs a wrapper. Radix closes
            a tooltip when its trigger is pressed; preventing it keeps the reason up. */}
        <TooltipTrigger asChild>
          <div
            ref={rowRef}
            className="w-full cursor-not-allowed [&_*]:cursor-not-allowed"
            tabIndex={0}
            onPointerDown={event => event.preventDefault()}
            onClick={event => event.preventDefault()}
          >
            {radio}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          onPointerDownOutside={event => {
            if (rowRef.current?.contains(event.target)) event.preventDefault();
          }}
        >
          {disabledReason}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const RuleChoice = ({ page, rule, orderBumps, bumpsLoading, onChange }) => {
  const radioPrefix = useId();
  const copy = scopeCopy(page);
  const bumpOptions = pageBumpOptions(orderBumps, rule.bumpIds);

  return (
    <div className="flex flex-col gap-3">
      <RadioGroup
        value={rule.appliesTo}
        onValueChange={appliesTo => onChange(switchRuleMode(rule, appliesTo))}
        aria-label={`What the coupon discounts on ${page.name}`}
        className="gap-2"
      >
        {pageModes(page).map(mode => (
          <ModeRadio
            key={mode}
            id={`${radioPrefix}-${mode}`}
            mode={mode}
            label={copy.modes[mode]}
            disabled={takesBumps(mode) && (bumpsLoading || bumpOptions.length === 0)}
            disabledReason={bumpsLoading ? null : copy.noBumps}
          />
        ))}
      </RadioGroup>
      {takesItems(rule.appliesTo) && (
        <>
          <p className="text-xs text-muted-foreground">{copy.help[rule.appliesTo]}</p>
          <RuleItems page={page} rule={rule} onChange={onChange} />
          {takesBumps(rule.appliesTo) && (
            <>
              <WordDivider word="and" />
              <RuleBumps page={page} rule={rule} bumpOptions={bumpOptions} onChange={onChange} />
            </>
          )}
        </>
      )}
    </div>
  );
};

const PageRules = ({ page, onChange }) => {
  const { orderBumps, isLoading } = usePageBumps(page);
  const updateRule = (index, patch) =>
    onChange({
      rules: page.rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    });

  return (
    <div className="flex flex-col gap-3">
      {page.rules.map((rule, index) => (
        <Fragment key={index}>
          {index > 0 && <WordDivider word="or" />}
          <RuleChoice
            page={page}
            rule={rule}
            orderBumps={orderBumps}
            bumpsLoading={isLoading}
            onChange={patch => updateRule(index, patch)}
          />
        </Fragment>
      ))}
    </div>
  );
};

const CouponScopePicker = () => {
  const { values, setFieldValue } = useFormikContext();
  const scopePages = values.scopePages ?? [];

  const setScopePages = next => setFieldValue('scopePages', next);
  const updatePage = (pageId, patch) =>
    setScopePages(scopePages.map(p => (p._id === pageId ? { ...p, ...patch } : p)));

  return (
    <div className="flex flex-col gap-1">
      <ScopeSearch
        pickedKeys={new Set(scopePages.map(page => scopeItemKey('page', page)))}
        onAddPage={page => setScopePages([...scopePages, newScopePage(page)])}
      />
      {scopePages.length > 0 && (
        <div className="mt-2 flex flex-col gap-2">
          {scopePages.map((page, index) => (
            <Fragment key={page._id}>
              {index > 0 && <WordDivider word="or" />}
              <PageMultiSelectPageContainer
                page={page}
                onRemove={removed => setScopePages(scopePages.filter(p => p._id !== removed._id))}
                onVisit={openDashboardLink}
              >
                <PageRules page={page} onChange={patch => updatePage(page._id, patch)} />
              </PageMultiSelectPageContainer>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default CouponScopePicker;
